import { fromJS, Set, Map, List } from 'immutable'
import { compose, curry } from 'ramda'
import {
  updateIn,
  set,
  deleteVal,
  subtract,
  union,
  add,
  safeToJS,
  push,
} from 'utils/immutable'
import * as referenceParameterActions from 'actions/referenceParameter.js'
import * as supportParameterActions from 'actions/supportParameter.js'
import createUri from 'utils/uriCreators'
import { createReducer } from 'reducers/utils'
import { isArray } from 'utils/types'

export const initialState = fromJS({
  isSearching: false,
  serviceDescriptionIsValid: true,
  itemDescriptionIsValid: true,
  selectedSourceRP: null,
  isDuplicating: false,
  importableSupportParameters: [],
  dictionary: {},
  rpBeingEdited: null,
  collectionMessage: {
    message: '',
    error: '',
  },
})

const handlers = {
  [referenceParameterActions.EDIT_REFERENCE_PARAMETER]: editReferenceParameter,
  [referenceParameterActions.SAVE_REFERENCE_PARAMETER_NOTES]: saveRPNotes,
  [referenceParameterActions.CANCEL_REFERENCE_PARAM_EDIT]:
    cancelReferenceParamEdit,
  [referenceParameterActions.LINK_SUPPORT_PARAM_TO_CURRENT_REFERENCE_PARAM]:
    linkSPToCurrentRP,
  [referenceParameterActions.RELINK_SUPPORT_PARAM_TO_CURRENT_REFERENCE_PARAM]:
    relinkSPToCurrentRP,
  [referenceParameterActions.UNLINK_SUPPORT_PARAM_FROM_CURRENT_REFERENCE_PARAM]:
    unlinkSPFromCurrentRP,
  [referenceParameterActions.UPDATE_REFERENCE_PARAM_FIELD]:
    updateReferenceParamField,
  [referenceParameterActions.SET_COMPATIBILITY_RULES_FOR_CURRENT_RP]:
    setCompatibilityModulesForCurrentRp,
  [referenceParameterActions.SET_CURR_RP_FIELD_VALIDITY]:
    setCurrRpFieldValidity,
  [referenceParameterActions.RESET_RP_STATE]: resetRpState,
  [referenceParameterActions.RESET_SELECTED_SOURCE_RP]: resetSelectedSourceRp,
  [referenceParameterActions.CLEAR_IMPORTABLE_SUPPORT_PARAMETERS]:
    clearImportableSupportParameters,
  [referenceParameterActions.SELECT_SOURCE_RP]: selectSourceRp,
  [referenceParameterActions.SET_SP_ACTION_LINK]: setSpActionLink,
  [referenceParameterActions.SET_SP_ACTION_DUPLICATE]: setSpActionDuplicate,
  [referenceParameterActions.TOGGLE_IMPORT_SP_SELECTED_STATE]:
    toggleImportSpSelectedState,
  [referenceParameterActions.TOGGLE_ALL_IMPORT_SP_SELECTED_STATE]:
    toggleAllImportSpSelectedState,
  [referenceParameterActions.SOURCE_RP_SUPPORT_PARAMS_RECEIVED]:
    sourceRpSupportParamsReceived,
  [referenceParameterActions.FETCHING_SOURCE_RP_SUPPORT_PARAMS]:
    fetchingSourceRpSupportParams,
  [referenceParameterActions.GET_REFERENCE_PARAMETER]: getReferenceParameter,
  [referenceParameterActions.SAVE_REFERENCE_PARAM]: saveReferenceParameter,
  [supportParameterActions.GET_LINKED_RPS]: getLinkedRPs,
  [supportParameterActions.SAVE_SUPPORT_PARAM]: updateRpBeingEditedWithSPSave,
  [supportParameterActions.DUPLICATE_SUPPORT_PARAM]:
    updateRpBeingEditedWithSPDuplicate,
  [referenceParameterActions.GET_REFERENCE_PARAMETER_NOTES]: getRPNotes,
  [referenceParameterActions.DUPLICATE_REFERENCE_PARAM]:
    duplicateReferenceParameter,
  [referenceParameterActions.UPDATE_RP_STATUS]: getStatusReferenceParameter,
  [referenceParameterActions.BATCH_IMPORT_COMPLETE]: completeBatchImport,
  [supportParameterActions.CANCEL_SUPPORT_PARAM_EDIT]: cancelSupportParamEdit,
  [referenceParameterActions.FETCH_SUPPORT_PARAMS_SUCCESS]:
    sourceRpSupportParamsReceived,
}

export default createReducer(initialState, handlers)

const addToSPsSet = (uri, collection = 'supportParameters') =>
  isArray(uri)
    ? updateIn(['rpBeingEdited', collection], union(uri))
    : updateIn(['rpBeingEdited', collection], add(uri))
const removeFromSPsSet = (uri, collection = 'supportParameters') =>
  isArray(uri)
    ? updateIn(['rpBeingEdited', collection], subtract(uri))
    : updateIn(['rpBeingEdited', collection], deleteVal(uri))

const updateReferenceParameterInDictionary = curry(
  (uri, referenceParameter, state) => {
    if (!referenceParameter) return state
    return state.updateIn(['dictionary', uri], (entry = Map()) =>
      entry.merge(
        fromJS({
          ...referenceParameter,
          supportParameters: Set(referenceParameter.supportParameters),
          /** properties below will need to be moved to the eventual "currently editing RP" then this function can go away */
          compatibilityModules: Set(referenceParameter.compatibilityModules),
          removedSupportParameters: Set([]),
          // To hold newly created SPs the user has 'canceled'. Remove from RP when saving or canceling edit of RP
          addedSupportParameters: Set([]),
        })
      )
    )
  }
)

function cancelSupportParamEdit(state, { supportParameter }) {
  const rpBeingEdited = safeToJS(state.get('rpBeingEdited'))
  if (!rpBeingEdited || !supportParameter) {
    return state
  }
  const { originalSPUri, uri } = supportParameter
  if (originalSPUri) {
    return compose(
      removeFromSPsSet(uri),
      removeFromSPsSet(uri, 'addedSupportParameters')
    )(state)
  }
  return state
}

function updateRpBeingEditedWithSPSave(state, { supportParameter }) {
  const rpBeingEdited = safeToJS(state.get('rpBeingEdited'))
  if (!rpBeingEdited) {
    return state
  }
  const { uri } = supportParameter

  if (rpBeingEdited.supportParameters.includes(uri)) {
    return state.setIn(['rpBeingEdited', 'spHasBeenEdited'], true)
  }
  return state
}

function completeBatchImport(state, { supportParameters }) {
  const uris = supportParameters.map(({ uri }) => uri)
  return compose(
    addToSPsSet(uris),
    addToSPsSet(uris, 'addedSupportParameters')
  )(state)
}

function updateRpBeingEditedWithSPDuplicate(state, { data }) {
  const { result: uri } = data
  const rpBeingEdited = safeToJS(state.get('rpBeingEdited'))
  if (!rpBeingEdited) {
    return state
  }

  return compose(
    addToSPsSet(uri),
    addToSPsSet(uri, 'addedSupportParameters')
  )(state)
}

function editReferenceParameter(state, { rpUrl }) {
  const currParam = state.getIn(['dictionary', rpUrl])

  return state.merge({
    rpBeingEdited: currParam,
  })
}

function getStatusReferenceParameter(state, { data }) {
  const uri = data.result
  const referenceParameter = data.entities.referenceParameters[uri]
  return updateReferenceParameterInDictionary(uri, referenceParameter)(state)
}

function getReferenceParameter(state, { data }) {
  const uri = data.result
  const referenceParameter = data.entities.referenceParameters[uri]
  return updateReferenceParameterInDictionary(uri, referenceParameter)(state)
}

function saveReferenceParameter(state, { data }) {
  const {
    result: uri,
    entities: { referenceParameters },
  } = data

  const referenceParameter = referenceParameters[uri]
  const updateRP = updateReferenceParameterInDictionary(uri, referenceParameter)
  const unsetrpBeingEdited = set('rpBeingEdited', null)

  return compose(unsetrpBeingEdited, updateRP)(state)
}

function duplicateReferenceParameter(state, { data }) {
  const {
    entities: { referenceParameters },
    result: uri,
  } = data
  const referenceParameter = referenceParameters[uri]
  const updateRP = updateReferenceParameterInDictionary(uri, referenceParameter)
  const setrpBeingEdited = set('rpBeingEdited', {
    ...referenceParameter,
    supportParameters: Set(referenceParameter.supportParameters),
    /** properties below will need to be moved to the eventual "currently editing RP" then this function can go away */
    compatibilityModules: Set(referenceParameter.compatibilityModules),
    removedSupportParameters: Set([]),
    // To hold newly created SPs the user has 'canceled'. Remove from RP when saving or canceling edit of RP
    addedSupportParameters: Set([]),
  })

  return compose(setrpBeingEdited, updateRP)(state)
}

function getLinkedRPs(state, { data }) {
  const { referenceParameters } = data
  const mappedRPs = referenceParameters.reduce((dict, rp) => {
    const { uri } = rp
    return {
      ...dict,
      [uri]: rp,
    }
  }, {})
  return state.mergeIn(['dictionary'], mappedRPs)
}

function cancelReferenceParamEdit(state) {
  return state.merge({
    rpBeingEdited: null,
  })
}

function saveRPNotes(state, { data, referenceParameterNumber, releaseNumber }) {
  return updateIn(
    [
      'dictionary',
      createUri.referenceParameter({
        referenceParameterNumber,
        releaseNumber,
      }),
      'notes',
    ],
    (list) => (list ? push(data)(list) : List([data]))
  )(state)
}

function getRPNotes(
  state,
  { data = [], referenceParameterNumber, releaseNumber }
) {
  return state.setIn(
    [
      'dictionary',
      createUri.referenceParameter({
        referenceParameterNumber,
        releaseNumber,
      }),
      'notes',
    ],
    fromJS(data)
  )
}

function linkSPToCurrentRP(state, { spUri }) {
  return compose(
    addToSPsSet(spUri),
    addToSPsSet(spUri, 'addedSupportParameters')
  )(state)
}

function relinkSPToCurrentRP(state, { spUri }) {
  return compose(
    addToSPsSet(spUri),
    removeFromSPsSet(spUri, 'removedSupportParameters')
  )(state)
}
function unlinkSPFromCurrentRP(state, { spUri }) {
  return compose(
    addToSPsSet(spUri, 'removedSupportParameters'),
    removeFromSPsSet(spUri, 'addedSupportParameters')
  )(state)
}

function updateReferenceParamField(state, { fieldName, newValue }) {
  return state.setIn(['rpBeingEdited', fieldName], newValue)
}

function setCompatibilityModulesForCurrentRp(state, { rules }) {
  return state.setIn(['rpBeingEdited', 'compatibilityModules'], fromJS(rules))
}

function setCurrRpFieldValidity(state, { fieldName, isValidForSaving }) {
  switch (fieldName) {
    case 'serviceDescription':
      return state.set('serviceDescriptionIsValid', isValidForSaving)
    case 'itemDescription':
      return state.set('itemDescriptionIsValid', isValidForSaving)
    default:
      return state
  }
}

function resetRpState(state) {
  return state.merge({
    isSearching: false,
    serviceDescriptionIsValid: true,
    itemDescriptionIsValid: true,
    selectedSourceRP: null,
    rpBeingEdited: null,
  })
}

function resetSelectedSourceRp(state) {
  return state.merge({
    selectedSourceRP: null,
    importableSupportParameters: [],
  })
}

function clearImportableSupportParameters(state) {
  return state.merge({
    importableSupportParameters: [],
  })
}

function selectSourceRp(state, { rp }) {
  return state.set('selectedSourceRP', fromJS(rp))
}

function setSpActionLink(state, { uri }) {
  const supportParams = state.get('importableSupportParameters')
  if (!supportParams) {
    return state
  }

  return state.setIn(['importableSupportParameters', uri, 'action'], 'link')
}

function setSpActionDuplicate(state, { uri }) {
  const supportParams = state.get('importableSupportParameters')
  if (!supportParams) {
    return state
  }

  return state.setIn(
    ['importableSupportParameters', uri, 'action'],
    'duplicate'
  )
}

function toggleImportSpSelectedState(state, { uri }) {
  return state.updateIn(
    ['importableSupportParameters', uri, 'selected'],
    (selectedSupportParams) => !selectedSupportParams
  )
}

function toggleAllImportSpSelectedState(state) {
  const supportParams = state.get('importableSupportParameters')
  if (!supportParams) {
    return state
  }

  const absoluteSelectedValue = supportParams.some((param) =>
    param.get('selected')
  )

  return state.set(
    'importableSupportParameters',
    supportParams.map((sp) => sp.set('selected', !absoluteSelectedValue))
  )
}
function sourceRpSupportParamsReceived(state, { data }) {
  const { supportParameters } = data.entities
  const spData = Object.entries(supportParameters).reduce(
    (dict, [key, val]) => ({
      ...dict,
      [key]: {
        selected: true,
        action: val.status === 'Active' ? 'link' : 'duplicate',
      },
    }),
    {}
  )
  const result = {
    isSearching: false,
    importableSupportParameters: spData,
  }

  return state.merge(result)
}

function fetchingSourceRpSupportParams(state) {
  return state.set('isSearching', true)
}
